import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Event, Club, Registration } from '../types';
import { isClubAdmin, isStudent, isSuperAdmin } from '../utils/roleUtils';
import { ROLE_DASHBOARD_MAP } from '../constants/roles';
import { cache } from '../utils/cache';
import { invalidateEventsCache } from '../utils/cacheInvalidation';
import { TrashIcon, CloseIcon, TwitterIcon, FacebookIcon, LinkedInIcon, WhatsAppIcon, LinkIcon, ShareIcon } from './Icons';
 import { uploadImageToCdn } from '../lib/uploadImage';
import { BACKEND_URL } from '../lib/backendUrl';

const API_URL = BACKEND_URL;

const toArray = <T,>(payload: any, keys: string[]): T[] => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key] as T[];
  }
  return [];
};

interface CustomField {
  headline: string;
  value: string;
}

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ show: boolean; eventId: string | null; eventName: string }>({
    show: false,
    eventId: null,
    eventName: ''
  });
  const [shareMenuOpen, setShareMenuOpen] = useState<string | null>(null);
  const { token, user } = useAuth();
  const [imagePreview, setImagePreview] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const [eventForm, setEventForm] = useState({
    name: '',
    description: '',
    type: 'event' as Event['type'],
    club_id: '',
    fee: 0,
    date: '',
    image: '',
    location: '',
    registrationDeadline: '',
    eligibility: '',
    teamSize: 4,
    customFields: [] as CustomField[],
  });
  const [customFieldDraft, setCustomFieldDraft] = useState<CustomField>({ headline: '', value: '' });

  const fetchEvents = async () => {
    try {
      // Generate cache key based on authentication
      const cacheKey = token ? 'events_authenticated' : 'events_public';
      
      // Check if we have cached data
      const cachedData = cache.get<Event[]>(cacheKey);
      if (cachedData) {
        console.log('Using cached events data');
        setEvents(cachedData);
        return;
      }

      console.log('Fetching events from API');
      // If not logged in, use public endpoint; otherwise use protected endpoint
      const endpoint = token ? `${API_URL}/api/events` : `${API_URL}/public/events`;
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      
      const response = await axios.get(endpoint, config);
      const eventList = toArray<Event>(response.data, ['data', 'events', 'items']);
      setEvents(eventList);
      // Cache the data for 5 minutes
      cache.set(cacheKey, eventList, 5 * 60 * 1000);
    } catch (error) {
      console.error('Failed to fetch events', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      // Generate cache key based on user role and authentication
      const cacheKey = token 
        ? `clubs_${user?.role}_${user?.id}` 
        : 'clubs_public';
      
      // Check if we have cached data
      const cachedData = cache.get<Club[]>(cacheKey);
      if (cachedData) {
        console.log('Using cached clubs data');
        setClubs(cachedData);
        return;
      }

      console.log('Fetching clubs from API');
      // If not logged in, use public endpoint; otherwise use protected endpoint
      const endpoint = token ? `${API_URL}/api/clubs` : `${API_URL}/public/clubs`;
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      
      const response = await axios.get(endpoint, config);
      let clubList = toArray<Club>(response.data, ['data', 'clubs', 'items']);
      
      // For club admins, filter to show only their own clubs
      if (isClubAdmin(user?.role) && user?.id) {
        clubList = clubList.filter((club: any) => (club.created_by || club.createdBy) === user.id);
        console.log('Filtered clubs for club admin:', clubList);
      }
      
      setClubs(clubList);
      // Cache the data for 5 minutes
      cache.set(cacheKey, clubList, 5 * 60 * 1000);
    } catch (error) {
      console.error('Failed to fetch clubs', error);
      setClubs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async () => {
    // Only fetch registrations if user is logged in
    if (!token) {
      setRegistrations([]);
      return;
    }
    
    try {
      // Generate cache key for user registrations
      const cacheKey = `registrations_${user?.id}`;
      
      // Check if we have cached data
      const cachedData = cache.get<Registration[]>(cacheKey);
      if (cachedData) {
        console.log('Using cached registrations data');
        setRegistrations(cachedData);
        return;
      }

      console.log('Fetching registrations from API');
      const response = await axios.get(`${API_URL}/api/registrations/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const registrationList = toArray<Registration>(response.data, ['data', 'registrations', 'items']);
      setRegistrations(registrationList);
      // Cache the data for 3 minutes (shorter TTL for more dynamic data)
      cache.set(cacheKey, registrationList, 3 * 60 * 1000);
    } catch (error) {
      console.error('Failed to fetch registrations', error);
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchClubs();
    fetchRegistrations();
    
    console.log('EventsPage - User role:', user?.role);
    console.log('EventsPage - User ID:', user?.id);
    console.log('EventsPage - Is club admin?', isClubAdmin(user?.role));
    console.log('EventsPage - Is super admin?', isSuperAdmin(user?.role));
    console.log('EventsPage - Show create button?', isClubAdmin(user?.role) || isSuperAdmin(user?.role));
  }, [user?.role, user?.id, token]);

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const details = {
        location: eventForm.location,
        registrationDeadline: eventForm.registrationDeadline,
        eligibility: eventForm.eligibility,
        teamSize: eventForm.type === 'hackathon' ? eventForm.teamSize : undefined,
        customFields: eventForm.customFields,
      };

      const payload = {
        name: eventForm.name,
        description: eventForm.description,
        type: eventForm.type,
        club_id: eventForm.club_id,
        fee: eventForm.fee,
        date: eventForm.date,
        image: eventForm.image,
        details: JSON.stringify(details),
      };

      const response = await axios.post(`${API_URL}/api/events`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowCreateEvent(false);
      setEventForm({
        name: '',
        description: '',
        type: 'event',
        club_id: '',
        fee: 0,
        date: '',
        image: '',
        location: '',
        registrationDeadline: '',
        eligibility: '',
        teamSize: 4,
        customFields: [],
      });
      setCustomFieldDraft({ headline: '', value: '' });
      setImagePreview('');
      // Clear cache to force refresh
      invalidateEventsCache();
      fetchEvents();
    } catch (error: any) {
      const errorMessage = error.response?.data || error.message || 'Failed to create event';
      console.error('Failed to create event:', errorMessage);
      alert(`Error: ${typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)}`);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const uploadedUrl = await uploadImageToCdn(file, token, 'events');
        setEventForm({ ...eventForm, image: uploadedUrl });
        setImagePreview(uploadedUrl);
      } catch (error: any) {
        alert(error?.message || 'Failed to upload image');
      }
    }
  };

  const addCustomField = () => {
    if (!customFieldDraft.headline.trim() || !customFieldDraft.value.trim()) {
      return;
    }
    setEventForm({
      ...eventForm,
      customFields: [...eventForm.customFields, { ...customFieldDraft }],
    });
    setCustomFieldDraft({ headline: '', value: '' });
  };

  const removeCustomField = (index: number) => {
    setEventForm({
      ...eventForm,
      customFields: eventForm.customFields.filter((_, i) => i !== index),
    });
  };

  const handleEnroll = async (eventId: string, fee: number) => {
    try {
      await axios.post(
        `${API_URL}/api/registrations`,
        { event_id: eventId, fee_paid: fee },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Registration confirmed! Please check your email and dashboard wallet.');
      fetchRegistrations();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Enrollment failed');
    }
  };

  const isRegistered = (eventId: string) => {
    return registrations.some((r) => r.event_id === eventId);
  };

  const handleShare = (eventId: string, eventName: string, platform: string) => {
    const currentUrl = `${window.location.protocol}//${window.location.host}/public/events/${eventId}`;
    const text = `Check out this event: ${eventName}`;
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(currentUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + currentUrl)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(currentUrl);
        alert('Link copied to clipboard!');
        break;
    }
    setShareMenuOpen(null);
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await axios.delete(`${API_URL}/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Remove from UI immediately without refreshing
      setEvents(events.filter(event => event.id !== eventId));
      setDeleteConfirmation({ show: false, eventId: null, eventName: '' });
      invalidateEventsCache();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete event';
      console.error('Failed to delete event:', errorMessage);
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleBackToDashboard = () => {
    if (user?.role) {
      const normalizedRole = user.role.toUpperCase().replace('-', '_') as keyof typeof ROLE_DASHBOARD_MAP;
      const dashboardPath = ROLE_DASHBOARD_MAP[normalizedRole];
      navigate(dashboardPath || '/');
    } else {
      navigate('/');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dashboard-content">
      {/* Back Button */}
      <motion.button
        onClick={handleBackToDashboard}
        className="back-button"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        ← BACK TO DASHBOARD
      </motion.button>

      <h2 className="dashboard-title">EVENTS</h2>

      {(isClubAdmin(user?.role) || isSuperAdmin(user?.role)) && (
        <div className="admin-actions">
          <button onClick={() => setShowCreateEvent(true)} className="cyber-button">
            CREATE NEW EVENT
          </button>
        </div>
      )}

      {showCreateEvent && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          className="modal-overlay" 
          onClick={() => setShowCreateEvent(false)}
        >
          <motion.div 
            initial={{ scale: 0.8, y: 50, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.8, y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="modal-content create-club-modal" 
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div 
              className="modal-header"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="modal-title">CREATE NEW EVENT</h3>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="modal-close-btn"
                onClick={() => setShowCreateEvent(false)}
                type="button"
              >
                <CloseIcon size={14} />
              </motion.button>
            </motion.div>

            <form onSubmit={handleCreateEvent} className="create-club-form">
              <motion.div 
                className="form-group"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <label className="form-label">
                  Event Name
                </label>
                <input
                  type="text"
                  placeholder="Enter event name..."
                  value={eventForm.name}
                  onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                  className="cyber-input enhanced-input"
                  required
                />
              </motion.div>

              <motion.div 
                className="form-group"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.25 }}
              >
                <label className="form-label">
                  Description
                </label>
                <textarea
                  placeholder="Describe your event..."
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  className="cyber-textarea enhanced-textarea"
                  rows={3}
                  required
                />
              </motion.div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <motion.div 
                  className="form-group"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <label className="form-label">
                    Event Type
                  </label>
                  <select
                    value={eventForm.type}
                    onChange={(e) => setEventForm({ ...eventForm, type: e.target.value as Event['type'] })}
                    className="cyber-select enhanced-input"
                    style={{ height: '50px' }}
                  >
                    <option value="event">Event</option>
                    <option value="workshop">Workshop</option>
                    <option value="exhibition">Exhibition</option>
                    <option value="seminar">Seminar</option>
                    <option value="competition">Competition</option>
                    <option value="hackathon">Hackathon</option>
                  </select>
                </motion.div>

                <motion.div 
                  className="form-group"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.35 }}
                >
                  <label className="form-label">
                    Select Club {isClubAdmin(user?.role) && <span style={{color: '#ff006e'}}>*</span>}
                  </label>
                  <select
                    value={eventForm.club_id}
                    onChange={(e) => setEventForm({ ...eventForm, club_id: e.target.value })}
                    className="cyber-select enhanced-input"
                    style={{ height: '50px' }}
                    required={isClubAdmin(user?.role)} // Only required for club admins
                  >
                    <option value="">{isClubAdmin(user?.role) ? 'Choose a club...' : 'College Event (No Club)'}</option>
                    {clubs.map((club) => (
                      <option key={club.id} value={club.id}>
                        {club.name}
                      </option>
                    ))}
                  </select>
                  {!isClubAdmin(user?.role) && (
                    <p style={{ fontSize: '0.8rem', color: '#00ffff', marginTop: '0.5rem', opacity: 0.7 }}>
                      Leave empty to create a college-hosted event
                    </p>
                  )}
                </motion.div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <motion.div 
                  className="form-group"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <label className="form-label">
                    Fee (₹)
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    value={eventForm.fee}
                    onChange={(e) => setEventForm({ ...eventForm, fee: Number(e.target.value) })}
                    className="cyber-input enhanced-input"
                    min="0"
                  />
                </motion.div>

                <motion.div 
                  className="form-group"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.45 }}
                >
                  <label className="form-label">
                    Event Date
                  </label>
                  <input
                    type="datetime-local"
                    value={eventForm.date}
                    onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                    className="cyber-input enhanced-input"
                    required
                  />
                </motion.div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <motion.div
                  className="form-group"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    placeholder="Event location"
                    value={eventForm.location}
                    onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    className="cyber-input enhanced-input"
                  />
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.52 }}
                >
                  <label className="form-label">Registration Deadline</label>
                  <input
                    type="datetime-local"
                    value={eventForm.registrationDeadline}
                    onChange={(e) => setEventForm({ ...eventForm, registrationDeadline: e.target.value })}
                    className="cyber-input enhanced-input"
                  />
                </motion.div>
              </div>

              <motion.div
                className="form-group"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.54 }}
              >
                <label className="form-label">Eligibility</label>
                <textarea
                  placeholder="Who can participate?"
                  value={eventForm.eligibility}
                  onChange={(e) => setEventForm({ ...eventForm, eligibility: e.target.value })}
                  className="cyber-textarea enhanced-textarea"
                  rows={2}
                />
              </motion.div>

              {eventForm.type === 'hackathon' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <motion.div
                    className="form-group"
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.56 }}
                  >
                    <label className="form-label">Team Size</label>
                    <input
                      type="number"
                      min="1"
                      value={eventForm.teamSize}
                      onChange={(e) => setEventForm({ ...eventForm, teamSize: Number(e.target.value) || 1 })}
                      className="cyber-input enhanced-input"
                    />
                  </motion.div>
                </div>
              )}

              <motion.div
                className="form-group"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.58 }}
              >
                <label className="form-label">Custom Fields (Headline + Value)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.75rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Headline (e.g., Problem Statement)"
                    value={customFieldDraft.headline}
                    onChange={(e) => setCustomFieldDraft({ ...customFieldDraft, headline: e.target.value })}
                    className="cyber-input enhanced-input"
                  />
                  <textarea
                    placeholder="Value"
                    value={customFieldDraft.value}
                    onChange={(e) => setCustomFieldDraft({ ...customFieldDraft, value: e.target.value })}
                    className="cyber-input enhanced-input"
                    rows={2}
                    style={{ resize: 'vertical', minHeight: '50px' }}
                  />
                  <button type="button" onClick={addCustomField} className="cyber-button-secondary">+ Add</button>
                </div>
                {eventForm.customFields.length > 0 && (
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {eventForm.customFields.map((field, idx) => (
                      <span key={idx} className="event-chip" style={{ whiteSpace: 'pre-wrap' }}>
                        <strong>{field.headline}:</strong> {field.value}
                        <button
                          type="button"
                          onClick={() => removeCustomField(idx)}
                          style={{ marginLeft: '0.5rem', background: 'transparent', border: 'none', color: '#ff006e', cursor: 'pointer' }}
                        >
                          x
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>

              <motion.div 
                className="form-group"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <label className="form-label">
                  Event Image
                </label>
                <div className="file-upload-container">
                  <input
                    type="file"
                    id="event-image-upload"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="file-input"
                  />
                  <label htmlFor="event-image-upload" className="file-upload-label">
                    {imagePreview ? 'Change Image' : 'Choose Image'}
                  </label>
                  <span className="file-hint">Max 5MB, JPG/PNG/GIF</span>
                </div>
                {imagePreview && (
                  <motion.div 
                    className="image-preview"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <img src={imagePreview} alt="Preview" />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview('');
                        setEventForm({ ...eventForm, image: '' });
                      }}
                      className="remove-image-btn"
                    >
                      Remove Image
                    </button>
                  </motion.div>
                )}
              </motion.div>

              <motion.div 
                className="modal-buttons"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.55 }}
              >
                <motion.button 
                  type="submit" 
                  className="cyber-button create-btn"
                  whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(255, 0, 110, 0.6)" }}
                  whileTap={{ scale: 0.95 }}
                >
                  CREATE EVENT
                </motion.button>
                <motion.button 
                  type="button" 
                  onClick={() => setShowCreateEvent(false)} 
                  className="cyber-button-secondary cancel-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  CANCEL
                </motion.button>
              </motion.div>
            </form>
          </motion.div>
        </motion.div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="cards-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="futuristic-card" style={{ minHeight: '480px' }}>
              <div className="card-background" style={{ background: 'linear-gradient(90deg, rgba(139,92,246,0.1) 0%, rgba(139,92,246,0.2) 50%, rgba(139,92,246,0.1) 100%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }}></div>
              <div className="card-content">
                <div style={{ height: '20px', background: 'rgba(139,92,246,0.2)', borderRadius: '4px', marginBottom: '10px', animation: 'pulse 1.5s infinite' }}></div>
                <div style={{ height: '60px', background: 'rgba(139,92,246,0.1)', borderRadius: '4px', animation: 'pulse 1.5s infinite 0.2s' }}></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && (
      <div className="cards-grid">
        {events.map((event) => (
          <motion.div 
            key={event.id} 
            whileHover={{ scale: 1.05 }} 
            className="futuristic-card"
          >
            {/* Card Background Image */}
            <div className="card-background">
              {event.image ? (
                <img src={event.image} alt={event.name} />
              ) : (
                <div className="card-background-placeholder">{(event.type || 'event').toUpperCase()}</div>
              )}
              <div className="card-image-overlay"></div>
            </div>
            
            {/* Badges positioned over image */}
            {event.image && (
              <>
                {/* Event Type Badge - Left Side */}
                <div style={{
                  position: 'absolute',
                  top: '-0.3rem',
                  left: '0.5rem',
                  zIndex: 10
                }}>
                  <div className="event-type-badge">{(event.type || 'other').toUpperCase()}</div>
                </div>
                
                {/* College Event Badge - Right Side */}
                {!event.club_id && (
                  <div style={{
                    position: 'absolute',
                    top: '-0.3rem',
                    right: '0.5rem',
                    zIndex: 10
                  }}>
                    <div style={{
                      display: 'inline-block',
                      backgroundColor: 'rgba(124, 58, 237, 0.7)',
                      border: '2px solid #7c3aed',
                      color: '#fff',
                      padding: '0.4rem 0.8rem',
                      borderRadius: '3px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      backdropFilter: 'blur(10px)',
                      textShadow: '0 0 5px rgba(124, 58, 237, 0.6)'
                    }}>
                       College Event
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Card Border Accent */}
            <div className="card-border-accent"></div>
            
            {/* Card Content */}
            <div className="card-content">
              {/* Vertical Stripe */}
              <div className="card-vertical-stripe"></div>
              
              {/* Event Name */}
              <h4 className="futuristic-card-title">{event.name}</h4>
              
              {/* Event Description */}
              <p className="futuristic-card-description">{event.description}</p>
              
              {/* Event Details */}
              <div className="event-chip-row">
                <span className="event-chip">Fee: ₹{event.fee}</span>
                <span className="event-chip">Date: {event.date ? new Date(event.date).toLocaleDateString() : 'TBA'}</span>
                {event.club_id && (
                  <span className="event-chip event-chip-club">
                    Club: {event.club_name || 'Loading...'}
                  </span>
                )}
              </div>
              
              {/* Card Footer with Button */}
              <div className="card-footer">
                {/* Club Admin Edit/Delete Options */}
                {(isClubAdmin(user?.role) || isSuperAdmin(user?.role)) && (
                  <>
                    <motion.button
                      onClick={() => {
                        const normalizedRole = (user?.role || 'CLUB_ADMIN').toUpperCase().replace('-', '_') as keyof typeof ROLE_DASHBOARD_MAP;
                        const dashboardPath = ROLE_DASHBOARD_MAP[normalizedRole] || '/dashboard/club';
                        navigate(`${dashboardPath}/events/${event.id}/edit`, { state: { event } });
                      }}
                      className="card-enter-btn"
                      style={{ flex: 1, backgroundColor: 'rgba(99, 102, 241, 0.8)' }}
                      whileHover={{ 
                        scale: 1.05,
                        backgroundColor: 'rgba(99, 102, 241, 1)',
                        boxShadow: "0 0 30px rgba(99, 102, 241, 0.8)"
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      EDIT
                    </motion.button>
                    <motion.button
                      onClick={() => navigate(`/public/events/${event.id}`)}
                      className="card-enter-btn"
                      style={{ flex: 1, backgroundColor: 'rgba(124, 58, 237, 0.8)' }}
                      whileHover={{ 
                        scale: 1.05,
                        backgroundColor: 'rgba(124, 58, 237, 1)',
                        boxShadow: "0 0 30px rgba(124, 58, 237, 0.8)"
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      VIEW
                    </motion.button>
                    <motion.button
                      onClick={() => setDeleteConfirmation({ show: true, eventId: event.id, eventName: event.name })}
                      className="card-delete-btn"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <TrashIcon size={16} color="#fff" />
                    </motion.button>
                  </>
                )}
                {isStudent(user?.role) && (
                  <>
                    {isRegistered(event.id) ? (
                      <div className="card-action-shell card-action-primary">
                        <button className="card-enter-btn enrolled" disabled>
                          ENROLLED
                        </button>
                      </div>
                    ) : (
                      <div className="card-action-shell card-action-primary">
                        <motion.button
                          onClick={() => handleEnroll(event.id, event.fee)}
                          className="card-enter-btn"
                          whileHover={{ boxShadow: "0 0 30px rgba(124, 58, 237, 0.8)" }}
                          whileTap={{ scale: 0.98 }}
                        >
                          ENROLL
                        </motion.button>
                      </div>
                    )}
                    <div className="card-action-shell card-action-secondary">
                      <motion.button
                        onClick={() => navigate(`/public/events/${event.id}`)}
                        className="card-enter-btn"
                        style={{ backgroundColor: 'rgba(124, 58, 237, 0.8)' }}
                        whileHover={{
                          backgroundColor: 'rgba(124, 58, 237, 1)',
                          boxShadow: "0 0 30px rgba(124, 58, 237, 0.8)"
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        EXPLORE
                      </motion.button>
                    </div>
                  </>
                )}
                {!user?.role && (
                  <>
                    <div className="card-action-shell card-action-primary">
                      <motion.button
                        className="card-enter-btn"
                        onClick={() => navigate('/auth')}
                        whileHover={{ boxShadow: "0 0 30px rgba(124, 58, 237, 0.8)" }}
                        whileTap={{ scale: 0.98 }}
                      >
                        ENROLL
                      </motion.button>
                    </div>
                    <div className="card-action-shell card-action-secondary">
                      <motion.button
                        className="card-explore-btn"
                        onClick={() => navigate(`/public/events/${event.id}`)}
                        whileHover={{ boxShadow: "0 0 30px rgba(139, 92, 246, 0.8)" }}
                        whileTap={{ scale: 0.98 }}
                      >
                        EXPLORE
                      </motion.button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setDeleteConfirmation({ show: false, eventId: null, eventName: '' })}
        >
          <motion.div
            className="delete-confirmation-modal modal-content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-title" style={{ color: '#ff3b3b', marginBottom: '1rem' }}>
              DELETE EVENT
            </h3>
            <p style={{ marginBottom: '1.5rem', color: '#ccc', lineHeight: '1.6' }}>
              Are you sure you want to delete <strong>{deleteConfirmation.eventName}</strong>? This action cannot be undone.
            </p>
            <div className="modal-buttons">
              <motion.button
                onClick={() => setDeleteConfirmation({ show: false, eventId: null, eventName: '' })}
                className="modal-cancel-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                CANCEL
              </motion.button>
              <motion.button
                onClick={() => deleteConfirmation.eventId && handleDeleteEvent(deleteConfirmation.eventId)}
                className="modal-confirm-delete-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                DELETE
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default EventsPage;
